import React from 'react';

import { FolderIcon } from '@vlabs/icons';
import {
  ApparentAgeFilterWithRightPopover,
  ApparentGenderFilterWithRightPopover,
  BackpackFilterWithRightPopover,
  AgeFilterWithRightPopover,
  GenderFilterWithRightPopover,
  HandlersFilterWithRightPopover,
  HeadwearFilterWithRightPopover,
  ListsFilterWithRightPopover,
  LocationFilterWithRightPopover,
  LowerAttributesFilterWithRightPopover,
  ShoesColorFilterWithRightPopover,
  SimilarityFilterWithRightPopover,
  SourceFilterWithRightPopover,
  UpperAttributesFilterWithRightPopover,
  useEventListFilters,
  useEventListFiltersTranslation,
} from '@vlabs/monokit/entities/events/filters';
import { filterIsApplied } from '@vlabs/monokit/shared/ui/filter-menu';
import { FiltersHeader } from '@vlabs/shared/components/filters-panel/FiltersHeader';
import {
  Badge, Control, Group, SideMenu, useWindowSize,
} from '@vlabs/uikit';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { WORKSPACE_DEFAULT_FILTERS } from './constants';
import { getWorkspaceFilterGroupCounts, hasLocationFilters } from './countAppliedFilters';
import st from './WorkspaceFiltersPanel.module.sass';

interface WorkspaceFiltersPanelProps {
  onCloseClick: () => void;
  className?: string;
}

function WorkspaceFiltersPanel({
  onCloseClick,
  className = undefined,
}: WorkspaceFiltersPanelProps): React.JSX.Element {
  const { t } = useTranslation();
  const { t: tFilters } = useEventListFiltersTranslation();
  const { filters, setFilters } = useEventListFilters();

  const windowSize = useWindowSize();
  const isCompact = windowSize.width < 1920;

  const groupCounts = getWorkspaceFilterGroupCounts(filters);

  return (
    <div className={cn(st.Root, className)}>
      <Group
        gapSize="0"
        vertical
      >
        <FiltersHeader
          customTitle={t('lunapoint:фильтры.обнаружение и совпадение')}
          mode="close"
          onClick={onCloseClick}
        />

        <SideMenu.MenuItem
          data-testid="workspace.commonFilters"
          isOpened={false}
          leftIcon={<FolderIcon />}
          rightIcon={groupCounts.general > 0 && <Badge value={groupCounts.general} />}
          value={tFilters('group_general')}
        >
          <SourceFilterWithRightPopover
            badge={filterIsApplied(!!filters.sources?.length)}
            testId="sourcesFilter"
            value={tFilters('sources')}
          />
          <HandlersFilterWithRightPopover
            badge={filterIsApplied(!!filters.handler_ids?.length)}
            testId="handlersFilter"
            value={tFilters('handler_ids')}
          />
          <SimilarityFilterWithRightPopover
            badge={filterIsApplied(!!(filters.top_similar_object_similarity__gte
              ?? filters.top_similar_object_similarity__lt))}
            testId="similarityFilter"
            value={tFilters('top_similar_object_similarity')}
          />
          <LocationFilterWithRightPopover
            badge={filterIsApplied(hasLocationFilters(filters))}
            testId="geolocationFilter"
            value={tFilters('location')}
          />
          <ListsFilterWithRightPopover
            badge={filterIsApplied(!!filters.top_matching_candidates_label)}
            testId="listFilter"
            value={tFilters('lists')}
          />
        </SideMenu.MenuItem>

        <div className={st.Divider} />

        <SideMenu.MenuItem
          data-testid="workspace.faceAttributesFilters"
          leftIcon={<FolderIcon />}
          rightIcon={groupCounts.face > 0 && <Badge value={groupCounts.face} />}
          value={tFilters('group_face_attributes')}
        >
          <GenderFilterWithRightPopover
            badge={filterIsApplied(filters.gender !== undefined)}
            testId="genderFilter"
            value={tFilters('gender')}
          />
          <AgeFilterWithRightPopover
            badge={filterIsApplied(
              filters.age__gte !== undefined || filters.age__lt !== undefined,
            )}
            testId="ageFilter"
            value={tFilters('age')}
          />
        </SideMenu.MenuItem>

        <div className={st.Divider} />

        <SideMenu.MenuItem
          data-testid="workspace.bodyAttributesFilters"
          leftIcon={<FolderIcon />}
          rightIcon={groupCounts.body > 0 && <Badge value={groupCounts.body} />}
          value={tFilters('group_body_attributes')}
        >
          <ApparentAgeFilterWithRightPopover
            badge={filterIsApplied(
              filters.apparent_age__gte !== undefined || filters.apparent_age__lt !== undefined,
            )}
            testId="apparentAgeFilter"
            value={tFilters('apparent_age')}
          />
          <ApparentGenderFilterWithRightPopover
            badge={filterIsApplied(!!filters.apparent_gender?.length)}
            testId="apparentGenderFilter"
            value={tFilters('apparent_gender')}
          />
          <LowerAttributesFilterWithRightPopover
            badge={filterIsApplied(
              !!(filters.lower_garment_colors?.length ?? filters.lower_garment_types?.length),
            )}
            testId="lowerFilter"
            value={tFilters('lower_garment_colors')}
          />
          <UpperAttributesFilterWithRightPopover
            badge={filterIsApplied(
              !!(filters.upper_clothing_colors?.length ?? filters.sleeve_lengths?.length),
            )}
            testId="upperFilter"
            value={tFilters('upper_clothing')}
          />
          <HeadwearFilterWithRightPopover
            badge={filterIsApplied(
              !!(filters.headwear_apparent_colors?.length ?? filters.headwear_states?.length),
            )}
            testId="headwearFilter"
            value={tFilters('headwear')}
          />
          <ShoesColorFilterWithRightPopover
            badge={filterIsApplied(!!filters.shoes_apparent_colors?.length)}
            testId="shoesFilter"
            value={tFilters('shoes_apparent_colors')}
          />
          <BackpackFilterWithRightPopover
            badge={filterIsApplied(!!filters.backpack_states?.length)}
            testId="backpackFilter"
            value={tFilters('backpack_states')}
          />
        </SideMenu.MenuItem>

        <div className={st.Divider} />
      </Group>

      <div className={st.ButtonPanel}>
        <Control.Button
          data-testid="FilterForm.clearButton"
          kind="negative"
          onClick={() => setFilters(WORKSPACE_DEFAULT_FILTERS)}
          size={isCompact ? 'small' : 'medium'}
        >
          {t('button.clear')}
        </Control.Button>
      </div>
    </div>
  );
}

export default WorkspaceFiltersPanel;
